import { useState, useEffect, useRef } from 'react';

export const useTabAuth = () => {
  const [authData, setAuthData] = useState({
    token: null,
    role: null,
    user: null,
    isLoggedIn: false
  });
  
  const tabIdRef = useRef(`tab_${Date.now()}_${Math.random()}`);
  const intervalRef = useRef(null);

  // Initialize tab-specific authentication
  useEffect(() => {
    // Generate unique tab ID
    const tabId = tabIdRef.current;
    
    // Store tab ID in sessionStorage (tab-specific)
    sessionStorage.setItem('currentTabId', tabId);
    
    // Check if this tab has its own auth data
    const tabAuthData = sessionStorage.getItem(`tabAuth_${tabId}`);
    if (tabAuthData) {
      const parsed = JSON.parse(tabAuthData);
      setAuthData(parsed);
    } else {
      // Fallback to localStorage (shared across tabs)
      const sharedToken = localStorage.getItem('access_token');
      const sharedRole = localStorage.getItem('role');
      const sharedUser = localStorage.getItem('user');
      
      if (sharedToken && sharedRole && sharedUser) {
        const authData = {
          token: sharedToken,
          role: sharedRole,
          user: JSON.parse(sharedUser),
          isLoggedIn: true
        };
        setAuthData(authData);
        // Store in sessionStorage for this tab
        sessionStorage.setItem(`tabAuth_${tabId}`, JSON.stringify(authData));
      }
    }

    // Cleanup on tab close
    const handleBeforeUnload = () => {
      sessionStorage.removeItem(`tabAuth_${tabId}`);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Check for auth changes from other tabs
    const checkAuthChanges = () => {
      const sharedToken = localStorage.getItem('access_token');
      const sharedRole = localStorage.getItem('role');
      const sharedUser = localStorage.getItem('user');
      
      if (sharedToken && sharedRole && sharedUser) {
        const newAuthData = {
          token: sharedToken,
          role: sharedRole,
          user: JSON.parse(sharedUser),
          isLoggedIn: true
        };
        
        // Only update if different from current tab's auth
        if (JSON.stringify(newAuthData) !== JSON.stringify(authData)) {
          setAuthData(newAuthData);
          sessionStorage.setItem(`tabAuth_${tabId}`, JSON.stringify(newAuthData));
        }
      } else if (authData.isLoggedIn) {
        // Clear auth if shared storage is cleared
        setAuthData({
          token: null,
          role: null,
          user: null,
          isLoggedIn: false
        });
        sessionStorage.removeItem(`tabAuth_${tabId}`);
      }
    };

    // Check every 2 seconds for auth changes
    intervalRef.current = setInterval(checkAuthChanges, 2000);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [authData]);

  const login = (token, role, user) => {
    const newAuthData = {
      token,
      role,
      user,
      isLoggedIn: true
    };
    
    setAuthData(newAuthData);
    
    // Store in both localStorage (shared) and sessionStorage (tab-specific)
    localStorage.setItem('access_token', token);
    localStorage.setItem('role', role);
    localStorage.setItem('user', JSON.stringify(user));
    
    const tabId = tabIdRef.current;
    sessionStorage.setItem(`tabAuth_${tabId}`, JSON.stringify(newAuthData));
    
    // Set axios header for this tab only
    if (window.axiosInstance) {
      window.axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  };

  const logout = () => {
    setAuthData({
      token: null,
      role: null,
      user: null,
      isLoggedIn: false
    });
    
    // Clear from both storages
    localStorage.removeItem('access_token');
    localStorage.removeItem('role');
    localStorage.removeItem('user');
    
    const tabId = tabIdRef.current;
    sessionStorage.removeItem(`tabAuth_${tabId}`);
    
    // Clear axios header for this tab
    if (window.axiosInstance) {
      delete window.axiosInstance.defaults.headers.common["Authorization"];
    }
  };

  return {
    ...authData,
    login,
    logout,
    tabId: tabIdRef.current
  };
};
